import React, { useEffect, useState } from "react";
import {
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Box,
  Paper,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  Badge,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon,
  Public as PublicIcon,
  AccessTime as AccessTimeIcon,
  SportsEsports as GameIcon,
  EmojiEvents as TrophyIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { editGroup, joinToGroup, listPublicGroups } from "../../store/actions";

const PublicGroupsUI = () => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  const publicGroups = useSelector((state) => state?.userProfile?.groups.list);
  const userId = useSelector((state) => state?.userProfile?.profile?.id);

  useEffect(() => {
    dispatch(listPublicGroups());
  }, [dispatch]);

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {}, [dispatch]);
  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((date - now) / (1000 * 60));

    if (diffInMinutes < 0) {
      return "Live Now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d`;
    }
  };

  // Helper function to check if group is currently running
  const isGroupRunning = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    return start <= now;
  };

  // Generate avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      "#10B981",
      "#3B82F6",
      "#8B5CF6",
      "#F59E0B",
      "#EF4444",
      "#06B6D4",
      "#84CC16",
      "#F97316",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleUserAdd = (group) => {
    dispatch(joinToGroup(group._id));
  };

  const filteredGroups =
    publicGroups?.filter((group) => {
      const matchesSearch = group.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesFilter =
        selectedFilter === "all" ||
        (selectedFilter === "live" && isGroupRunning(group.start_time)) ||
        (selectedFilter === "upcoming" && !isGroupRunning(group.start_time));
      return matchesSearch && matchesFilter;
    }) || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#0A0E1A",
        backgroundImage: `
          radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.1) 0%, transparent 50%)
        `,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "300px",
          background:
            "linear-gradient(180deg, rgba(102, 126, 234, 0.05) 0%, transparent 100%)",
          zIndex: 0,
        },
      }}
    >
      {/* Header Section */}
      <Container
        maxWidth="xl"
        sx={{ pt: 4, pb: 2, position: "relative", zIndex: 1 }}
      >
        {/* Enhanced Header */}
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              mb: 4,
              gap: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 3,
                textAlign: { xs: "left", sm: "unset" },
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <GameIcon sx={{ color: "white", fontSize: 32 }} />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    color: "white",
                    fontWeight: 800,
                    mb: 1,
                    letterSpacing: "-0.02em",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Public Groups
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#94A3B8",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  Discover and join gaming communities from around the world
                </Typography>
              </Box>
            </Box>

            {/* Stats Overview */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: "100%", maxWidth: 400 }}
            >
              <Paper
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(30, 41, 59, 0.8)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid #334155",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <TrophyIcon sx={{ color: "#10B981", fontSize: 20 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "white", fontWeight: 700, lineHeight: 1 }}
                  >
                    {filteredGroups.length}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748B", fontSize: "11px" }}
                  >
                    Active Groups
                  </Typography>
                </Box>
              </Paper>

              <Paper
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(30, 41, 59, 0.8)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid #334155",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <TrendingIcon sx={{ color: "#667eea", fontSize: 20 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "white", fontWeight: 700, lineHeight: 1 }}
                  >
                    {
                      filteredGroups.filter((g) => isGroupRunning(g.start_time))
                        .length
                    }
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748B", fontSize: "11px" }}
                  >
                    Live Now
                  </Typography>
                </Box>
              </Paper>
            </Stack>
          </Box>

          {/* Search and Filter Bar */}
        </Box>

        {/* Enhanced Groups Grid */}
        <Grid container spacing={3}>
          {filteredGroups?.map((group, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  backgroundColor: "rgba(30, 41, 59, 0.8)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid #334155",
                  borderRadius: "20px",
                  height: "100%",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-8px) scale(1.02)",
                    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
                    border: "1px solid #667eea",
                    backgroundColor: "rgba(30, 41, 59, 0.95)",
                    "&::before": {
                      opacity: 1,
                    },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
                    opacity: 0,
                    transition: "opacity 0.4s ease",
                    zIndex: 0,
                  },
                  "& > *": {
                    position: "relative",
                    zIndex: 1,
                  },
                }}
              >
                {/* Enhanced Card Header */}
                <Box
                  sx={{
                    p: 2,
                    position: "relative",
                    background: isGroupRunning(group.start_time)
                      ? "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)"
                      : "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%)",
                  }}
                >
                  {/* Floating Status Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    {isGroupRunning(group.start_time) && (
                      <Chip
                        icon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                        label="LIVE"
                        size="small"
                        sx={{
                          backgroundColor: "#10B981",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "11px",
                          height: "28px",
                          px: 1,
                          animation: "pulse 2s infinite",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
                          "@keyframes pulse": {
                            "0%, 100%": { opacity: 1, transform: "scale(1)" },
                            "50%": { opacity: 0.8, transform: "scale(1.05)" },
                          },
                        }}
                      />
                    )}
                  </Box>
                  {/* Group Title Section */}
                  <Box sx={{ pr: 8 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: "white",
                        fontWeight: 900,
                        mb: 2,
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                        fontSize: "1.5rem",
                        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      }}
                    >
                      {group.name}
                    </Typography>

                    {/* Organizer Badge */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        px: 3,
                        py: 1.5,
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,0.12)",
                        backdropFilter: "blur(8px)",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: getAvatarColor(
                            group.organizer_id?.username || "Unknown"
                          ),
                          fontSize: "12px",
                          fontWeight: 700,
                        }}
                      >
                        {(group.organizer_id?.username || "U")[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#9ca3af",
                            fontSize: "10px",
                            fontWeight: 500,
                            display: "block",
                            lineHeight: 1,
                          }}
                        >
                          Hosted by
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#10B981",
                            fontWeight: 700,
                            fontSize: "12px",
                            lineHeight: 1,
                          }}
                        >
                          {group.organizer_id?.username || "Unknown"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <CardContent sx={{ p: 4, pt: 2 }}>
                  {/* Enhanced Stats Grid */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          backgroundColor: "rgba(15, 23, 42, 0.6)",
                          border: "1px solid #1E293B",
                          textAlign: "center",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "rgba(16, 185, 129, 0.1)",
                            border: "1px solid rgba(16, 185, 129, 0.3)",
                          },
                        }}
                      >
                        <PeopleIcon
                          sx={{
                            color: "#10B981",
                            fontSize: 24,
                            mb: 1,
                          }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            color: "white",
                            fontWeight: 800,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {group.users?.length || 0}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontSize: "10px",
                            fontWeight: 600,
                          }}
                        >
                          PLAYERS
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={8}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          backgroundColor: "rgba(15, 23, 42, 0.6)",
                          border: "1px solid #1E293B",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: isGroupRunning(group.start_time)
                              ? "rgba(16, 185, 129, 0.1)"
                              : "rgba(245, 158, 11, 0.1)",
                            border: isGroupRunning(group.start_time)
                              ? "1px solid rgba(16, 185, 129, 0.3)"
                              : "1px solid rgba(245, 158, 11, 0.3)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <AccessTimeIcon
                            sx={{
                              color: isGroupRunning(group.start_time)
                                ? "#10B981"
                                : "#F59E0B",
                              fontSize: 20,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "white",
                              fontWeight: 700,
                              fontSize: "16px",
                            }}
                          >
                            {getRelativeTime(group.start_time)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontSize: "11px",
                            fontWeight: 500,
                          }}
                        >
                          {isGroupRunning(group.start_time)
                            ? "Currently Running"
                            : "Starting Soon"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Enhanced Schedule Card */}
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      backgroundColor: "rgba(15, 23, 42, 0.6)",
                      border: "1px solid #1E293B",
                      mb: 3,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "4px",
                        height: "100%",
                        background:
                          "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <ScheduleIcon
                        sx={{
                          color: "#667eea",
                          fontSize: 18,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748B",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Event Schedule
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      {formatDate(group.start_time)}
                    </Typography>
                  </Box>

                  {/* Enhanced Player Preview */}
                  {/* <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748B",
                        fontSize: "12px",
                        fontWeight: 600,
                        mb: 2,
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Active Players ({group.users?.length || 0})
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {group.users?.slice(0, 4).map((userId, idx) => (
                        <Avatar
                          key={idx}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: getAvatarColor(userId),
                            fontSize: "12px",
                            fontWeight: 700,
                            border: "2px solid #334155",
                            "&:hover": {
                              transform: "scale(1.1)",
                              zIndex: 10,
                              border: "2px solid #667eea",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {userId.slice(-2).toUpperCase()}
                        </Avatar>
                      ))}

                      {(group.users?.length || 0) > 4 && (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: "#667eea",
                            fontSize: "10px",
                            fontWeight: 700,
                            border: "2px solid #334155",
                          }}
                        >
                          +{(group.users?.length || 0) - 4}
                        </Avatar>
                      )}

                      {(group.users?.length || 0) === 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748B",
                            fontSize: "13px",
                            fontStyle: "italic",
                          }}
                        >
                          No players joined yet
                        </Typography>
                      )}
                    </Box>
                  </Box> */}

                  <Divider sx={{ mb: 3, borderColor: "#334155" }} />

                  {/* Enhanced Action Buttons */}
                  <Stack direction="row" spacing={2}>
                    {!group.users?.includes(userId) ? (
                      <Button
                        variant="contained"
                        onClick={() => handleUserAdd(group)}
                        startIcon={<PersonAddIcon sx={{ fontSize: 18 }} />}
                        fullWidth
                        sx={{
                          py: 2,
                          borderRadius: "16px",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "14px",
                          background:
                            "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                          border: "none",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 25px rgba(16, 185, 129, 0.4)",
                          },
                        }}
                      >
                        Join Group
                      </Button>
                    ) : (
                      <Button
                        disabled
                        fullWidth
                        sx={{
                          py: 2,
                          borderRadius: "16px",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "14px",
                          backgroundColor: "#374151",
                          color: "#9CA3AF",
                          cursor: "not-allowed",
                        }}
                      >
                        Already Joined
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      sx={{
                        minWidth: "56px",
                        width: "56px",
                        height: "56px",
                        borderRadius: "16px",
                        border: "2px solid #334155",
                        color: "#94A3B8",
                        backgroundColor: "rgba(30, 41, 59, 0.5)",
                        "&:hover": {
                          border: "2px solid #667eea",
                          backgroundColor: "#667eea",
                          color: "white",
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                        },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Enhanced Pagination */}
        {filteredGroups?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 6,
            }}
          >
            <Paper
              sx={{
                px: 4,
                py: 2,
                borderRadius: "16px",
                backgroundColor: "rgba(30, 41, 59, 0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid #334155",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#94A3B8",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Showing {filteredGroups.length} of {publicGroups?.length || 0}{" "}
                groups
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Enhanced Empty State */}
        {(!publicGroups || publicGroups?.length === 0) && (
          <Paper
            sx={{
              p: 8,
              textAlign: "center",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              backdropFilter: "blur(12px)",
              border: "1px solid #334155",
              borderRadius: "24px",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))",
              },
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "rgba(51, 65, 85, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 4,
                border: "2px solid #334155",
              }}
            >
              <PeopleIcon sx={{ fontSize: 60, color: "#64748B" }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: 700,
                mb: 2,
              }}
            >
              No Groups Available
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default PublicGroupsUI;
